import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo } from "react";
import toast from "react-hot-toast";

import { setAccessTokenGetter, setOnForbidden, setOnUnauthorized } from "../api/client";
import { queryClient } from "../api/queryClient";
import {
  clearCachedToken,
  getAccessToken,
  startTokenRefresh,
  stopTokenRefresh,
} from "../auth/accessToken";
import { AuthConfigurationError } from "../auth/AuthConfigurationError";
import { assertAuthConfigured, loginRequest } from "../config/authConfig";
import { setPendingAuthError } from "../config/msalInstance";
import type { AuthContextValue, AuthUser } from "./authContext";
import { AuthContext } from "./authContext";

/**
 * Auth provider backed by Microsoft Entra ID (MSAL). Establishes the signed-in
 * user's identity and wires the API client's token getter + 401 handler.
 * Authentication only — no roles/permissions.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const user = useMemo<AuthUser | null>(() => {
    if (!isAuthenticated || accounts.length === 0) return null;
    const account = instance.getActiveAccount() ?? accounts[0];
    return {
      id: account.localAccountId,
      email: account.username,
      displayName: account.name,
      tenantId: account.tenantId,
    };
  }, [isAuthenticated, accounts, instance]);

  // Only block the app shell during initial bootstrap / redirect handling.
  // AcquireToken (interactive silent via hidden iframe) must NOT be included —
  // doing so causes a render loop where the auth guard hides the page mid-query,
  // cancels in-flight requests, then shows it again indefinitely.
  const isLoading =
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect;

  const login = useCallback(() => {
    if (inProgress !== InteractionStatus.None) return;
    try {
      // Fail loudly when the build was not given the Entra app-registration
      // ids, rather than redirecting to Entra with an empty client id.
      assertAuthConfigured();
    } catch (error) {
      if (error instanceof AuthConfigurationError) {
        setPendingAuthError(error);
        return;
      }
      throw error;
    }
    void instance.loginRedirect(loginRequest);
  }, [instance, inProgress]);

  const logout = useCallback(async () => {
    queryClient.clear();
    stopTokenRefresh();
    clearCachedToken();
    await instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }, [instance]);

  /**
   * Best-effort silent sign-out: try to end the Entra session via a hidden
   * iframe; if blocked, fall back to a full logout redirect.
   */
  const silentLogout = useCallback(async () => {
    queryClient.clear();
    stopTokenRefresh();
    clearCachedToken();
    try {
      const authority =
        instance.getConfiguration().auth.authority ||
        "https://login.microsoftonline.com/common";
      const logoutUrl = `${authority}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(
        window.location.origin
      )}`;

      let iframeSucceeded = false;
      try {
        iframeSucceeded = await new Promise<boolean>((resolve) => {
          let settled = false;
          const iframe = document.createElement("iframe");
          iframe.style.display = "none";
          const settle = (ok: boolean) => {
            if (settled) return;
            settled = true;
            iframe.remove();
            resolve(ok);
          };
          iframe.addEventListener("load", () => settle(true));
          iframe.addEventListener("error", () => settle(false));
          setTimeout(() => settle(false), 3000);
          iframe.src = logoutUrl;
          document.body.appendChild(iframe);
        });
      } catch {
        iframeSucceeded = false;
      }

      await instance.clearCache();

      if (iframeSucceeded) {
        window.location.replace(window.location.origin);
      } else {
        await instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
      }
    } catch (error) {
      console.error("Silent logout failed, falling back to redirect logout:", error);
      try {
        await instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
      } catch {
        window.location.replace(window.location.origin);
      }
    }
  }, [instance]);

  // Wire API client: silent token getter for the signed-in account.
  useEffect(() => {
    setAccessTokenGetter(getAccessToken);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      startTokenRefresh();
    } else {
      stopTokenRefresh();
    }
    return () => stopTokenRefresh();
  }, [isAuthenticated]);

  // Wire 401: drop the cached token so the next request re-acquires.
  useEffect(() => {
    setOnUnauthorized(() => clearCachedToken());
    setOnForbidden(() => {
      toast.error("You do not have permission to perform this action.");
    });
    return () => {
      setOnUnauthorized(null);
      setOnForbidden(null);
    };
  }, []);

  /**
   * Derive avatar initials: first letters of the first and last name parts
   * ("John Doe" -> "JD"), falling back to the first two letters of a single
   * name, then the email, then a neutral placeholder. Parenthetical or
   * punctuation-only tokens (e.g. Entra names like "Jane Doe (Contractor)")
   * are ignored so initials never include stray symbols.
   */
  const getInitials = useCallback((): string => {
    const name = user?.displayName?.trim();
    if (name) {
      // Drop "(...)" segments, then keep only tokens that contain a letter.
      const parts = name
        .replace(/\([^)]*\)/g, " ")
        .split(/\s+/)
        .map((part) => part.match(/\p{L}/u)?.[0] ?? "")
        .filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0]}${parts[parts.length - 1]}`.toUpperCase();
      }
      if (parts.length === 1) {
        const single = name.replace(/[^\p{L}]/gu, "");
        if (single) return single.slice(0, 2).toUpperCase();
      }
    }
    const email = user?.email?.trim();
    if (email) {
      const letters = email.replace(/[^\p{L}]/gu, "");
      if (letters) return letters.slice(0, 2).toUpperCase();
    }
    return "U";
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      logout,
      silentLogout,
      getInitials,
    }),
    [user, isAuthenticated, isLoading, login, logout, silentLogout, getInitials]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
